import { AxiosResponse } from "axios";
import { zipWith } from "ramda";
import { call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";

import { IResolveResponse } from "../api/ResolveApi/resolve-api.types";
import { FetchIdsAndResolve, fetchIdsAndResolveThem, FetchResult } from "../App/Common/sagas";
import { IScormPackage } from "../App/types/common";
import { api } from "../dependencies";
import { hasErrorRecords, resolveScormPackagePostSuccessResponseValueToScormPackageUIData } from "../parsers/resolve";
import { SagaAction } from "../sagas/types";
import * as selectors from "../selectors/selectors";
import { SagaFunction } from "./types";

export interface IFetchAllScormPackagesFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export type FetchScormPackages = FetchResult<{
  scormPackageIdsResponse: AxiosResponse<string[]>;
  scormPackagesResponse: AxiosResponse<Array<IResolveResponse<IScormPackage>>>;
}>;

export function* fetchAndResolveScormPackages(accountId: string, organizationId: string) {
  const fetchResolver = fetchIdsAndResolveThem({
    getEditableByRelativeTo: api.scormPackages.getEditableByRelativeTo,
    resolve: api.resolve.scormPackage.post,
  });

  const [err, resp]: FetchIdsAndResolve = yield call(fetchResolver(accountId, organizationId));
  if (err || !resp) {
    return [err, resp];
  }
  const [ids, resolvedData] = resp;
  return [err, { scormPackageIdsResponse: ids, scormPackagesResponse: resolvedData }];
}

export const fetchAllScormPackages: (
  furtherActions: IFetchAllScormPackagesFurtherActions
) => SagaFunction = furtherActions =>
  function* _fetchAllScormPackages(_: IAction): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const [scormPackagesError, scormPackagesResponses]: FetchScormPackages = yield call(
      fetchAndResolveScormPackages,
      accountId,
      organizationId
    );

    if (scormPackagesError || !scormPackagesResponses) {
      return yield put(furtherActions.onFailure(scormPackagesError));
    }

    const { scormPackageIdsResponse, scormPackagesResponse } = scormPackagesResponses;

    const data = zipWith(
      (uuid, datum) => ({
        ...resolveScormPackagePostSuccessResponseValueToScormPackageUIData(datum),
        scormPackageId: uuid,
      }),
      scormPackageIdsResponse.data,
      scormPackagesResponse.data
    );
    yield put(furtherActions.onSuccess(data));
  };

export interface IFetchScormPackageFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const fetchScormPackage: (furtherActions: IFetchScormPackageFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchScormPackage(action: IAction): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const [scormPackagesError, scormPackagesResponse] = yield call(api.resolve.scormPackage.post, accountId, [
      action.scormPackageId,
    ]);
    if (scormPackagesError) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to fetch scormPackage by id.",
        })
      );
    } else if (hasErrorRecords(scormPackagesResponse.data)) {
      yield put(furtherActions.onFailure({ message: "ScormPackage contained errors." }));
    } else {
      const data = zipWith(
        (uuid, datum) => ({
          ...resolveScormPackagePostSuccessResponseValueToScormPackageUIData(datum),
          scormPackageId: uuid,
        }),
        [action.scormPackageId],
        scormPackagesResponse.data
      );
      yield put(furtherActions.onSuccess(data[0]));
    }
  };
